// First-party cookie-consent for the dashboard. Shares the form's `lime_consent`
// contract (same name, version, `.lime-booking.com` domain) so one ePrivacy
// cookie-storage decision carries across Lime web surfaces. The DB is the
// authoritative record (see the ConsentContext / `/consent` endpoint); this
// cookie is the fast client-side mirror that gates trackers before a round-trip.

export const CONSENT_COOKIE_NAME = "lime_consent";

// Bump when the categories or their meaning change — a stored decision with an
// older version is treated as "not decided" so the banner reappears.
export const CONSENT_VERSION = "1";

const COOKIE_MAX_AGE_DAYS = 180;

export type ConsentCategories = {
  analytics: boolean;
  marketing: boolean;
};

export type StoredConsent = ConsentCategories & { version: string };

// --- Pure helpers (string in / value out), unit-tested in the node env ---

export function readCookieValue(
  cookieString: string,
  name: string,
): string | null {
  const match = cookieString.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
  if (!match?.[2]) return null;
  try {
    return decodeURIComponent(match[2]);
  } catch {
    // A malformed percent-encoding (a hand-edited or truncated cookie) must not
    // throw — treat it as no value so the banner falls back to undecided.
    return null;
  }
}

export function parseStoredConsent(raw: string | null): StoredConsent | null {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<StoredConsent>;
    if (
      typeof parsed.analytics !== "boolean" ||
      typeof parsed.marketing !== "boolean" ||
      parsed.version !== CONSENT_VERSION
    ) {
      return null;
    }

    return {
      analytics: parsed.analytics,
      marketing: parsed.marketing,
      version: parsed.version,
    };
  } catch {
    return null;
  }
}

export function serializeConsent(categories: ConsentCategories): string {
  const value: StoredConsent = { ...categories, version: CONSENT_VERSION };
  return JSON.stringify(value);
}

// Maps the Complianz CMP's per-category cookies (`cmplz_<category>`, value
// "allow" when granted) to our categories. `statistics` ↔ `analytics`. Returns
// null when neither cookie exists — i.e. no decision was made through Complianz.
// (signupTracking.ts keeps its own reader in Complianz's `{ marketing,
// statistics }` shape for its GTM consent-mode replay.)
export function parseComplianzCookies(
  cookieString: string,
): ConsentCategories | null {
  const marketing = readCookieValue(cookieString, "cmplz_marketing");
  const statistics = readCookieValue(cookieString, "cmplz_statistics");
  if (marketing === null && statistics === null) return null;

  return {
    analytics: statistics === "allow",
    marketing: marketing === "allow",
  };
}

// --- DOM wrappers (thin; exercised in the browser, not the node unit env) ---

function cookieDomain(): string | undefined {
  // Share across *.lime-booking.com (dashboard + booking form); keep bare hosts
  // (localhost, preview deploys) scoped to themselves.
  const match = /(?:^|\.)(lime-booking\.com)$/.exec(window.location.hostname);
  return match ? `.${match[1]}` : undefined;
}

export function readConsentCookie(): StoredConsent | null {
  return parseStoredConsent(
    readCookieValue(document.cookie, CONSENT_COOKIE_NAME),
  );
}

export function writeConsentCookie(categories: ConsentCategories): void {
  const domain = cookieDomain();
  const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
  const secure = window.location.protocol === "https:";

  document.cookie =
    `${CONSENT_COOKIE_NAME}=${encodeURIComponent(serializeConsent(categories))}` +
    `; Max-Age=${maxAge}; Path=/; SameSite=Lax` +
    (domain ? `; Domain=${domain}` : "") +
    (secure ? "; Secure" : "");
}

export function readComplianzConsent(): ConsentCategories | null {
  return parseComplianzCookies(document.cookie);
}

export function readSessionToken(): string {
  try {
    return (localStorage.getItem("usr_token") ?? "").replaceAll('"', "");
  } catch {
    return "";
  }
}

export function hasSessionToken(): boolean {
  return readSessionToken() !== "";
}

// Where the client's current consent decision came from. `"cookie"` means it was
// hydrated from an existing (per-browser, possibly another user's) lime_consent
// cookie — which must NOT be treated as the logged-in user's own decision.
export type ConsentSource = "dashboard_banner" | "complianz" | "cookie";

// The subset that may be written to the DB — `"cookie"` (an inherited per-browser
// decision) is client-only and is never persisted as a user's consent.
export type PersistableConsentSource = Exclude<ConsentSource, "cookie">;

export type AuthenticatedConsentAction =
  | { kind: "apply-server" }
  | { kind: "apply-complianz" }
  | { kind: "keep" }
  | { kind: "reset" };

/**
 * Decides how to reconcile a logged-in user's authoritative DB consent snapshot
 * with whatever the client resolved locally. The user's DB record is the source
 * of truth; a user with no record must decide for themselves (banner), so an
 * inherited per-browser cookie is never adopted as their consent. Pure so it can
 * be unit-tested without the DOM.
 */
export function resolveAuthenticatedConsent(input: {
  serverDecided: boolean;
  localDecided: boolean;
  localSource: ConsentSource;
  complianz: ConsentCategories | null;
}): AuthenticatedConsentAction {
  if (input.serverDecided) return { kind: "apply-server" };
  if (input.localDecided && input.localSource === "dashboard_banner") {
    return { kind: "keep" };
  }
  if (input.complianz) return { kind: "apply-complianz" };
  return { kind: "reset" };
}

/**
 * Whether this banner instance asks for consent. An impersonating admin is never
 * asked, and while a page embeds the banner the floating one stays hidden.
 */
export function shouldShowConsentBanner(input: {
  hydrated: boolean;
  decided: boolean;
  impersonated: boolean;
  preferencesOpen: boolean;
  embedded: boolean;
  isBannerEmbedded: boolean;
}): boolean {
  if (!input.hydrated || input.decided || input.impersonated) return false;
  if (input.preferencesOpen) return false;

  return input.embedded || !input.isBannerEmbedded;
}
